import { ArchetypeScores } from "../types";

const SCORE_RANGE = {
  MIN: 0,
  MAX: 5,
  TOTAL_MAX: 225, // Maximum possible total score per archetype
};

export function validateScores(scores: ArchetypeScores): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const archetypes = [
    "jaguar",
    "condor",
    "llama",
    "quetzal",
    "colibri",
    "coqui",
    "buho",
  ];

  if (!scores) {
    return { isValid: false, errors: ["Scores object is required"] };
  }

  // Validate individual scores
  archetypes.forEach((archetype) => {
    const score = scores[archetype as keyof ArchetypeScores];

    if (typeof score !== "number" || isNaN(score)) {
      errors.push(`Invalid score for ${archetype}: ${score}`);
    } else {
      // Check range
      if (score < SCORE_RANGE.MIN) {
        errors.push(`Score for ${archetype} cannot be negative: ${score}`);
      }
      if (score > SCORE_RANGE.TOTAL_MAX) {
        errors.push(
          `Score for ${archetype} exceeds maximum possible: ${score}`
        );
      }
    }
  });

  // Validate total scores make sense
  const totalScore = Object.values(scores).reduce(
    (sum, score) => sum + (score || 0),
    0
  );
  if (totalScore === 0) {
    errors.push("Total score cannot be zero");
  }
  if (totalScore > SCORE_RANGE.TOTAL_MAX * archetypes.length) {
    errors.push(`Total score ${totalScore} exceeds maximum possible`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function validateUserData(data: {
  firstName?: string;
  lastName?: string;
  email?: string;
  jobTitle?: string;
  employerName?: string;
}): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  if (!data) {
    return { isValid: false, errors: { general: "User data is required" } };
  }

  // Validate first name
  if (!data.firstName?.trim()) {
    errors.firstName = "First name is required";
  } else if (data.firstName.length > 100) {
    errors.firstName = "First name is too long";
  }

  // Validate last name
  if (!data.lastName?.trim()) {
    errors.lastName = "Last name is required";
  } else if (data.lastName.length > 100) {
    errors.lastName = "Last name is too long";
  }

  // Validate email with regex (matches the database constraint)
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  if (!data.email?.trim()) {
    errors.email = "Email is required";
  } else if (!emailRegex.test(data.email)) {
    errors.email = "Please enter a valid email address";
  }

  // Validate job title
  if (!data.jobTitle?.trim()) {
    errors.jobTitle = "Job title is required";
  } else if (data.jobTitle.length > 200) {
    errors.jobTitle = "Job title is too long";
  }

  // Validate employer name
  if (!data.employerName?.trim()) {
    errors.employerName = "Employer name is required";
  } else if (data.employerName.length > 200) {
    errors.employerName = "Employer name is too long";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateDomainScores(scores: number[]): {
  isValid: boolean;
  error?: string;
} {
  if (!Array.isArray(scores)) {
    return { isValid: false, error: "Invalid scores array" };
  }

  const totalScore = scores.reduce((sum, score) => sum + (score || 0), 0);
  const averageScore = totalScore / scores.length;

  // Check for invalid values
  const hasInvalidScores = scores.some(
    (score) =>
      typeof score !== "number" || isNaN(score) || score < 0 || score > 100
  );

  if (hasInvalidScores) {
    return { isValid: false, error: "Invalid score values detected" };
  }

  // Check for unrealistic distribution
  if (averageScore < 10 || averageScore > 90) {
    return { isValid: false, error: "Unrealistic score distribution" };
  }

  return { isValid: true };
}
